// Верхній вузол схеми — точка приєднання до мережі.
//
// Іконка — власний SVG розподільчої шафи (корпус, секції, ввідний вимикач),
// а НЕ опора ЛЕП: на сонячних станціях точка приєднання це комірка КРУ, і
// вишка вводила б в оману. Remix-іконки тут не підходять — у наборі немає
// нічого ближчого за «розетку», що ще гірше.
import i18n from '@/i18n';
import { scada, stateColor, STATE_LABEL } from '@/scada/tokens';
import type { SchemeConnectionPoint } from './types';

const fmtPower = (kw: number) =>
  kw >= 1000 ? { v: (kw / 1000).toFixed(2), u: i18n.t('ak_scada2_mw') } : { v: String(Math.round(kw)), u: i18n.t('ak_scada2_kw') };

/** Розподільча шафа: корпус, дві секції, шина вводу, символ вимикача. */
function SwitchgearIcon({ color }: { color: string }) {
  return (
    <svg width="46" height="46" viewBox="0 0 46 46" fill="none" aria-hidden="true">
      <rect x="6.5" y="9.5" width="33" height="29" rx="2.5"
        stroke={scada.textDim} strokeWidth="1.4" fill={scada.panel2} />
      <line x1="23" y1="9.5" x2="23" y2="38.5" stroke={scada.border} strokeWidth="1.2" />
      {/* Ввід зверху */}
      <line x1="23" y1="4" x2="23" y2="9.5" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
      {/* Символ вимикача в лівій секції */}
      <line x1="14.5" y1="17" x2="14.5" y2="22" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
      <line x1="14.5" y1="24.5" x2="18" y2="30" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="14.5" cy="23.2" r="1.3" fill={color} />
      {/* Секційні шини в правій */}
      <line x1="28" y1="18" x2="35" y2="18" stroke={scada.textMuted} strokeWidth="1.2" strokeLinecap="round" />
      <line x1="28" y1="23" x2="35" y2="23" stroke={scada.textMuted} strokeWidth="1.2" strokeLinecap="round" />
      <line x1="28" y1="28" x2="35" y2="28" stroke={scada.textMuted} strokeWidth="1.2" strokeLinecap="round" />
      {/* Ніжки корпусу */}
      <line x1="12" y1="38.5" x2="12" y2="41.5" stroke={scada.textDim} strokeWidth="1.4" strokeLinecap="round" />
      <line x1="34" y1="38.5" x2="34" y2="41.5" stroke={scada.textDim} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

export default function ConnectionPoint({ data }: { data: SchemeConnectionPoint }) {
  const c = stateColor(data.state);
  const p = fmtPower(data.powerKw);
  const limit = data.limitKw != null ? fmtPower(data.limitKw) : null;

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 14,
      background: scada.panel, border: `1px solid ${scada.border}`,
      borderRadius: 12, padding: '12px 16px', boxShadow: 'var(--shadow)',
    }}>
      <SwitchgearIcon color={c} />

      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, color: scada.textMuted, marginBottom: 3 }}>{i18n.t('ak_scada2_point_of_connection')}</div>
        <div className="flex items-baseline" style={{ gap: 5 }}>
          <span className="font-display tabular-nums"
            style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: scada.text }}>{p.v}</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: scada.textMuted }}>{p.u}</span>
        </div>
      </div>

      <div style={{ width: 1, alignSelf: 'stretch', background: scada.border }} />

      <div style={{ display: 'grid', gap: 5 }}>
        <div>
          <div style={{ fontSize: 10, color: scada.textMuted }}>{i18n.t('ak_scada2_limit')}</div>
          <div className="tabular-nums" style={{ fontSize: 12, color: scada.textDim }}>
            {limit ? `${limit.v} ${limit.u}` : '—'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: scada.textMuted }}>{i18n.t('ak_scada2_frequency')}</div>
          <div className="tabular-nums" style={{ fontSize: 12, color: scada.textDim }}>{data.freqHz} {i18n.t('ak_scada2_hz')}</div>
        </div>
      </div>

      <div className="flex items-center" style={{ gap: 6, alignSelf: 'flex-end' }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: c, flexShrink: 0 }} />
        <span className="font-display" style={{
          fontSize: 10, fontWeight: 600, letterSpacing: '.05em',
          textTransform: 'uppercase', color: c,
        }}>{STATE_LABEL[data.state]}</span>
      </div>
    </div>
  );
}
